import React, { useCallback, useMemo } from 'react';
import {
	View,
	Platform,
} from 'react-native';
import { Picker as NativePicker } from '@react-native-picker/picker';
import { Feather } from '@expo/vector-icons';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { useIsOnCard } from '../../design/fieldSurface';

import PickerButton from './PickerButton/PickerButton';

const IS_WEB = Platform.select({ web: true, default: false });
const IS_IOS_NATIVE = Platform.OS === 'ios' && !IS_WEB;

// ActionSheetIOS is not available on web, so we need to check for it
let ActionSheetIOS;
if (IS_IOS_NATIVE) {
	({ ActionSheetIOS } = require('react-native'));
}

const Picker = ({
	options,
	selectedValue,
	onValueChange,
	styles: passedStyles,
	disabled,
	emptyValueLabel = 'Change',
	width,
	onActionSheetOpen,
	onActionSheetClose,
	accessibilityHint,
	accessibilityLabel,
	...passThroughProps
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const isOnCard = useIsOnCard();
	const { mode: providedMode, ...restPassThroughProps } = passThroughProps;
	const resolvedPickerMode = providedMode || (Platform.OS === 'android' ? 'dropdown' : undefined);
	const containerWidth = width || 100;
	const hasSelectedValue = selectedValue !== null && selectedValue !== undefined && selectedValue !== '';

	// Create options with empty value for Android
	const optionsWithEmpty = useMemo(() => {
		const selectedValueExists = hasSelectedValue && options.some(option => option.value === selectedValue);

		// Only add empty option if selectedValue is null/undefined OR doesn't exist in options
		if (!hasSelectedValue || !selectedValueExists) {
			return [
				{ label: emptyValueLabel, value: IS_WEB ? '' : null },
				...options,
			];
		}

		return options;
	}, [options, selectedValue, emptyValueLabel, hasSelectedValue]);

	// Resolve the actual selected value for the picker
	const resolvedSelectedValue = useMemo(() => {
		// If selectedValue is null/undefined, use empty string for web compatibility
		if (selectedValue === null || selectedValue === undefined) {
			return IS_WEB ? '' : null;
		}

		// Check if selectedValue exists in options
		const valueExists = options.some(option => option.value === selectedValue);

		// If value doesn't exist in options, use empty string on web, null on native
		return valueExists ? selectedValue : (IS_WEB ? '' : null);
	}, [selectedValue, options]);

	const selectedLabel = useMemo(() => {
		if (!hasSelectedValue) {
			return emptyValueLabel;
		}
		return options.find(option => option.value === selectedValue)?.label || 'Unknown value';
	}, [hasSelectedValue, emptyValueLabel, options, selectedValue]);

	const openSortOptions = useCallback(() => {
		if (ActionSheetIOS) {
			if (onActionSheetOpen) {
				onActionSheetOpen();
			}
			ActionSheetIOS.showActionSheetWithOptions(
				{
					options: ['Cancel', ...options.map(option => option.label)],
					cancelButtonIndex: 0,
				},
				(buttonIndex) => {
					if (buttonIndex > 0) {
						onValueChange(options[buttonIndex - 1].value);
					}
					if (onActionSheetClose) {
						onActionSheetClose(buttonIndex === 0);
					}
				},
			);
		} else {
			// For web, we'll use the native picker instead
			console.warn('ActionSheetIOS is not available on web platform');
		}
	}, [options, onValueChange, onActionSheetOpen, onActionSheetClose]);

	const handleValueChange = useCallback((value) => {
		// Don't call onValueChange if user selects the empty option
		// On web, empty string represents null
		if (IS_WEB ? value !== '' : value !== null) {
			onValueChange(value);
		}
	}, [onValueChange]);

	return (
		<View style={passedStyles}>
			{
				IS_IOS_NATIVE && (
					<PickerButton
						accessibilityHint={accessibilityHint}
						accessibilityLabel={accessibilityLabel}
						accessibilityRole={accessibilityLabel ? 'button' : undefined}
						style={[styles.nativePicker, { width: containerWidth }]}
						onPress={openSortOptions}
						disabled={disabled}
					>
						{selectedLabel}
					</PickerButton>
				)
			}
			{
				(!IS_IOS_NATIVE || IS_WEB) && (
					<View style={[styles.picker, isOnCard && styles.onCard, { width: containerWidth }]}>
						<NativePicker
							accessibilityHint={accessibilityHint}
							accessibilityLabel={accessibilityLabel}
							selectedValue={resolvedSelectedValue}
							onValueChange={handleValueChange}
							style={[styles.nativePicker, isOnCard && styles.onCard]}
							enabled={!disabled}
							dropdownIconColor={theme.ui.colors.textSecondary}
							mode={resolvedPickerMode}
							{...restPassThroughProps}
						>
							{
								optionsWithEmpty.map(option => (
									<NativePicker.Item
										key={option.value}
										label={option.label}
										value={option.value}
										style={styles.nativePickerItem}
									/>
								))
							}
						</NativePicker>
						{/* Web: the browser arrow is hidden; the same chevron as every other selector field. */}
						{IS_WEB && (
							<Feather name="chevron-down" size={18} color={theme.ui.colors.textSecondary} style={styles.webChevron} />
						)}
					</View>
				)
			}
		</View>
	);
};

// 44 high on every platform (touch target); colours and type from the redesign tokens.
const createStyles = ({ ui: { colors, radius, touch, type } }) => ({
	picker: {
		// Wrap picker with styled view, because React Native's API
		// does not yet allow full styling of the picker
		backgroundColor: colors.card,
		borderRadius: radius.card,
		borderColor: colors.borderStrong,
		borderWidth: 1,
		height: touch.min,
		justifyContent: 'center',
		overflow: 'hidden',
	},
	// Inside a Card the card-coloured fill would vanish (design/fieldSurface.js).
	onCard: {
		backgroundColor: colors.canvas,
	},
	webChevron: {
		position: 'absolute',
		right: 14,
		top: (touch.min - 2 - 18) / 2,
		pointerEvents: 'none',
	},
	nativePicker: {
		minWidth: 100,
		color: colors.text,
		backgroundColor: colors.card,
		...(Platform.OS === 'ios' && { minHeight: touch.min }),
		...(Platform.OS === 'web' && {
			width: '100%',
			height: '100%',
			borderWidth: 0,
			paddingLeft: 14,
			paddingRight: 40,
			fontSize: type.rowText.fontSize,
			fontFamily: type.rowText.fontFamily,
			appearance: 'none',
			cursor: 'pointer',
		}),
	},
	nativePickerItem: {
		color: colors.text,
		backgroundColor: colors.card,
	},
});

export default Picker;
